import { GoogleIcon } from '@mote/assets/icons'
import type { SystemProps } from '@xstyled/styled-components'
import styled from '@xstyled/styled-components'
import { Block } from '../block/block'
import { Button } from '../button'
import { Link } from '../link'
import { H3, Text } from '../typography/text'

type AuthSize = 'sm' | 'md' | 'lg'

type AuthStyle = {
  title: SystemProps['fontSize']
  subtitle: SystemProps['fontSize']
  caption: SystemProps['fontSize']
  cta: 'sm' | 'md' | 'lg'
  redirect: SystemProps['fontSize']
}

type AuthSizes = Record<AuthSize, AuthStyle>

const sizes: AuthSizes = {
  sm: {
    title: '2xl',
    subtitle: 'sm',
    caption: 'sm',
    cta: 'sm',
    redirect: 'sm'
  },
  md: {
    title: '3xl',
    subtitle: 'md',
    caption: 'md',
    cta: 'lg',
    redirect: 'md'
  },
  lg: {
    title: '4xl',
    subtitle: 'md',
    caption: 'md',
    cta: 'lg',
    redirect: 'md'
  }
}

export type AuthFormProps = {
  size?: AuthSize
  centered?: boolean
  title: string
  subtitle: string
  caption: string
  cta: {
    text: string
    action: () => void
  }
  redirect: {
    text: string
  } & (
    | {
        action: () => void
        urlPath?: never
      }
    | {
        urlPath: string
        action?: never
      }
  )
}

export const AuthForm = (props: AuthFormProps) => {
  const styles = sizes[props.size || 'md']
  const [containerAlign, textAlign] = props.centered
    ? (['center', 'center'] as const)
    : (['flex-start', 'left'] as const)

  return (
    <Root
      layout='vertical'
      align={containerAlign}
      justify='center'
      gap={32}
      spreadX
    >
      <Block gap={24} spreadX>
        <H3
          fontSize={styles.title}
          lineHeight={styles.title}
          fontWeight='bold'
          textAlign={textAlign}
        >
          {props.title}
        </H3>

        <Text
          color='grey.d2'
          fontWeight='normal'
          fontSize={styles.subtitle}
          lineHeight={styles.subtitle}
          textAlign={textAlign}
        >
          {props.subtitle}
        </Text>
      </Block>

      <Block gap={24} spreadX>
        <Button
          //@ts-expect-error
          icon={<GoogleIcon />}
          text={props.cta.text}
          size={styles.cta}
          variant='secondary'
          onClick={props.cta.action}
          block
        />

        <Text
          display='flex'
          alignItems='center'
          gap={1}
          fontSize={styles.caption}
          lineHeight={styles.caption}
          color='grey.d2'
          justifyContent={containerAlign}
        >
          {props.caption}
          {props.redirect.action ? (
            <Button
              //@ts-expect-error
              variant='link'
              size={styles.redirect}
              onClick={props.redirect.action}
              text={props.redirect.text}
            />
          ) : (
            <Link
              to={props.redirect.urlPath}
              fontSize='inherit'
              fontWeight='inherit'
              lineHeight='inherit'
              color={{
                _: 'primary.base',
                hover: 'primary.d1'
              }}
            >
              {props.redirect.text}
            </Link>
          )}
        </Text>
      </Block>
    </Root>
  )
}

const Root = styled(Block)`
  max-width: 370px;
`
